import React, { useState } from "react";

const TOKEN_PATTERN = /(@\[[^\]]+\]|(?:https?:\/\/|www\.)[^\s]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s]*)?|\b\d{10,}\b)/gi;

const DOMAIN_PATTERN = /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s]*)?$/i;
const NUMBER_PATTERN = /^\d{10,}$/;

const normalizeUrl = (url) => {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return `https://${url}`;
};

const isUrl = (value) => /^(https?:\/\/|www\.)/i.test(value) || DOMAIN_PATTERN.test(value);
const isCopyableNumber = (value) => NUMBER_PATTERN.test(value);

const escapeRegExp = (value = "") =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildParts = (text) => {
  const value = String(text || "");
  const parts = [];
  let lastIndex = 0;

  value.replace(TOKEN_PATTERN, (match, _group, offset) => {
    if (offset > lastIndex) {
      parts.push({
        type: "text",
        value: value.slice(lastIndex, offset),
      });
    }

    const isMention = /^@\[.+\]$/.test(match);
    parts.push({
      type: isMention ? "mention" : isCopyableNumber(match) ? "number" : isUrl(match) ? "url" : "text",
      value: isMention ? match.slice(2, -1) : match, // strip @[ and ]
    });

    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < value.length) {
    parts.push({
      type: "text",
      value: value.slice(lastIndex),
    });
  }

  return parts;
};

const renderHighlightedValue = (value, highlight, keyPrefix) => {
  const normalizedHighlight = String(highlight || "").trim();

  if (!normalizedHighlight) {
    return value;
  }

  const matcher = new RegExp(`(${escapeRegExp(normalizedHighlight)})`, "gi");
  const segments = String(value).split(matcher);

  if (segments.length === 1) {
    return value;
  }

  return segments.map((segment, index) =>
    segment.toLowerCase() === normalizedHighlight.toLowerCase() ? (
      <mark key={`${keyPrefix}-highlight-${index}`} className="chat-search-mark">
        {segment}
      </mark>
    ) : (
      <React.Fragment key={`${keyPrefix}-text-${index}`}>{segment}</React.Fragment>
    )
  );
};

const copyNumber = async (value) => {
  if (!navigator?.clipboard?.writeText) {
    return false;
  }

  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (error) {
    console.error("Failed to copy number:", error);
    return false;
  }
};

const LinkifiedText = ({ text = "", className = "", highlight = "" }) => {
  const parts = buildParts(text);
  const [copiedValue, setCopiedValue] = useState("");

  const handleCopyNumber = async (value) => {
    const copied = await copyNumber(value);

    if (!copied) {
      return;
    }

    setCopiedValue(value);

    window.clearTimeout(window.__chatCopyNumberTimer);
    window.__chatCopyNumberTimer = window.setTimeout(() => {
      setCopiedValue((currentValue) => (currentValue === value ? "" : currentValue));
    }, 1500);
  };

  return (
    <span className={["chat-formatted-text", className].filter(Boolean).join(" ")}>
      {parts.map((part, index) =>
        part.type === "mention" ? (
          <span key={`mention-${index}`} className="chat-mention-tag">
            @{part.value}
          </span>
        ) : part.type === "url" ? (
          <a
            key={`${part.value}-${index}`}
            href={normalizeUrl(part.value)}
            target="_blank"
            rel="noreferrer"
            className="chat-linkified-anchor chat-formatted-inline"
          >
            {renderHighlightedValue(part.value, highlight, `url-${index}`)}
          </a>
        ) : part.type === "number" ? (
          <span
            key={`${part.value}-${index}`}
            className="chat-copyable-number-group chat-formatted-inline"
          >
            <button
              type="button"
              className="chat-copyable-number"
              onClick={() => handleCopyNumber(part.value)}
              title="Copy phone number"
            >
              {renderHighlightedValue(part.value, highlight, `number-${index}`)}
            </button>
            <button
              type="button"
              className="chat-copy-number-icon"
              onClick={() => handleCopyNumber(part.value)}
              aria-label={`Copy ${part.value}`}
              title="Copy phone number"
            >
              <i className="bi bi-copy"></i>
            </button>
            {copiedValue === part.value && (
              <span className="chat-copy-number-feedback">Copied</span>
            )}
          </span>
        ) : (
          <React.Fragment key={`${part.value}-${index}`}>
            {renderHighlightedValue(part.value, highlight, `text-${index}`)}
          </React.Fragment>
        )
      )}
    </span>
  );
};

export default LinkifiedText;
